import { ApolloError } from '@apollo/client'
import { css } from '@emotion/react'
import { Alert, AlertType, HFlow, Link, VFlow } from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { confirm } from 'components/modals/confirm'
import { METRICS_ENABLED } from 'config/util'
import { useLoginMutation } from 'graphql/hooks.generated'
import { AtmosphereTransportManager } from 'hooks/atmosphere/AtmosphereTransportManager'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useBrowserStorage } from 'hooks/useStorage'
import qs from 'qs'
import React, { useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'
import { notify } from 'util/multitab'

import { sanitizeLoginForm } from './converter-login'
import { GovBrButton } from './GovBrButton'
import { LoginForm, LoginFormModel } from './LoginForm'
import { LoginSeparator } from './LoginSeparator'

export function LoginView() {
  const location = useLocation()
  const history = useHistory()
  const { performance } = useFirebase()
  const { clearPreviousVersionsEntries } = useBrowserStorage()
  const [login] = useLoginMutation()
  const { refresh: refreshSession } = useSession()
  const {
    configuracoes: { smtpConfigurado, internetHabilitada, linkInstalacao, govBREnabled, versao },
  } = useConfiguracoes()
  const [error, setError] = useState<ApolloError>()

  const traceEnabled = METRICS_ENABLED && internetHabilitada
  const esqueciSenhaDisponivel = internetHabilitada && smtpConfigurado && !!linkInstalacao

  const loginTrace = useMemo(() => {
    const newTrace = performance.trace('Login')
    if (process.env.REACT_APP_VERSION) {
      newTrace?.putAttribute('Versao', process.env.REACT_APP_VERSION)
    }
    newTrace?.putAttribute(
      'Resolucao',
      window.screen.width * window.devicePixelRatio + 'x' + window.screen.height * window.devicePixelRatio
    )
    if (traceEnabled) {
      newTrace?.start()
    }
    return newTrace
  }, [performance, traceEnabled])

  const searchParams = qs.parse(location.search, { ignoreQueryPrefix: true })

  const onLoginSuccess = async () => {
    AtmosphereTransportManager.getInstance().reset()
    await refreshSession()
    notify('LOGIN')
    window.localStorage.removeItem('NEWS')
    if (traceEnabled) loginTrace?.stop()
    if (process.env.REACT_APP_VERSION !== versao) {
      window.location.reload()
    }
    clearPreviousVersionsEntries()
    history.push('/')
  }

  const doLogin = (form: LoginFormModel) => {
    const sanitizedForm = sanitizeLoginForm(form)
    return login({
      variables: { input: sanitizedForm },
      context: {
        headers: {
          'Api-Consumer-Id': 'ESUS_WEB_CLIENT',
        },
      },
    })
      .then(onLoginSuccess)
      .catch((result) => {
        const resultError = result?.graphQLErrors?.[0]?.extensions?.classification
        if (resultError === 'UsuarioJaLogadoException' || resultError === 'PecAuthenticationException') {
          const doForceLogin = () => doLogin({ ...sanitizedForm, force: true })
          confirm({
            title: result?.graphQLErrors?.[0]?.message,
            cancelLabel: 'Cancelar',
            confirmLabel: 'Continuar',
            onConfirm: doForceLogin,
          })()
        } else {
          setError(result)
        }
      })
  }

  return (
    <VFlow
      style={css`
        min-height: 21rem;
      `}
    >
      {searchParams?.error && <SimpleError errorMessage={searchParams.error} />}
      {error && <Errors error={error} />}
      {searchParams.sessionExpired !== undefined && (
        <Alert
          type='info'
          inline
          styles={{
            container: css`
              padding: 0.5rem 0;
            `,
          }}
        >
          Sua sessão expirou. Informe seu usuário e senha novamente.
        </Alert>
      )}
      {searchParams.emailEnviado !== undefined && (
        <Alert
          type='warning'
          inline
          styles={{
            container: css`
              padding: 0.5rem 0;
            `,
          }}
        >
          E-mail de redefinição de senha enviado. Caso não tenha recebido, entre em contato com o coordenador da sua
          UBS.
        </Alert>
      )}
      <LoginForm onLogin={doLogin} />

      {esqueciSenhaDisponivel && (
        <Link component={RouterLink} to='/recuperarSenha'>
          Esqueci minha senha
        </Link>
      )}

      {internetHabilitada && govBREnabled && (
        <VFlow vSpacing={2.5}>
          <LoginSeparator />
          <HFlow justifyContent='center'>
            <GovBrButton href='/oauth2/authorization/govbr' />
          </HFlow>
        </VFlow>
      )}
    </VFlow>
  )
}

interface ErrorsProps {
  error: ApolloError
}

const Errors = (props: ErrorsProps) => {
  const { error } = props
  return (
    <React.Fragment>
      {error.graphQLErrors
        .filter((graphqlError) => {
          const errorInfo = errorMap[graphqlError.extensions?.classification as string] || { show: true }

          return errorInfo.show
        })
        .map((graphqlError, index) => {
          const errorInfo = errorMap[graphqlError.extensions?.classification as string] || { type: 'danger' }
          return (
            <Alert key={index} type={errorInfo.type} inline={true}>
              {graphqlError.message}
            </Alert>
          )
        })}
    </React.Fragment>
  )
}

interface SimpleErrorProps {
  errorMessage: string
}

const SimpleError = (props: SimpleErrorProps) => {
  return (
    <Alert type='danger' inline={true}>
      {props.errorMessage}
    </Alert>
  )
}

interface ErrorInfo {
  show: boolean
  type: AlertType
}

const errorMap: { [key: string]: ErrorInfo } = {
  BadCredentialsException: { type: 'warning', show: true },
  UsuarioJaLogadoException: { type: 'danger', show: false },
  PecAuthenticationException: { type: 'danger', show: false },
}
